import {
    DecisionTraceEntry,
    DeliveryProvider,
    Driver,
    OperatingContext,
    Order,
    SmartSplitSuggestion,
    SuggestedRoute,
    SystemConfig
} from '../../../types';
import { findBestProviderQuote, calculateInternalCostV2 } from '../costs/calculator';
import { FleetSlot, getFleetAvailability } from '../fleet/availability';
import { DistanceService } from '../core/distanceService';

const DELIVERY_PENDING_STATUSES = new Set<Order['status']>([
    'confirmed',
    'scheduled_confirmed',
    'ready',
    'assigned',
    'dispatched'
]);

const OWN_CAPACITY_BUFFER_RATIO = 0.85;
const DEFAULT_ORDER_TIME_LIMIT_MINUTES = 60;

type ManualMode = 'own' | 'third';

type SimplifiedDecision = {
    order: Order;
    reason: string;
    ownEtaMinutes?: number;
    thirdEtaMinutes?: number;
    ownSlackMinutes?: number;
    thirdSlackMinutes?: number;
};

const createEmptySuggestion = (): SmartSplitSuggestion => ({
    ownFleet: [],
    thirdParty: [],
    heldOrders: [],
    routes: [],
    thirdPartyTripsCount: 0,
    metrics: {
        breakdown: { ownCash: 0, thirdPartyCash: 0, ownDecision: 0, thirdPartyDecision: 0 },
        cashDelta: { chosen: 0, alternative: 0, signedDelta: 0, label: 'ECONOMIA_CAIXA', reason: 'UNKNOWN' },
        decisionStats: { totalDecisions: 0, byReason: {}, byMode: {} }
    },
    isSplitRecommended: false,
    optimizationConstrainedBySLA: false,
    estimatedTime: 0,
    minSla: 999,
    lowestSlack: 999,
    decisionTrace: [],
    suggestedStrategies: []
});

const getOrderTimeLimit = (order: Order): number => {
    const value = Number(order.timeLimit);
    if (!Number.isFinite(value) || value <= 0) return DEFAULT_ORDER_TIME_LIMIT_MINUTES;
    return value;
};

const getElapsedMinutes = (order: Order, nowMs: number): number => {
    const createdAt = Number(order.createdAt) || nowMs;
    return Math.max(0, (nowMs - createdAt) / 60000);
};

const getDeadlineTimestamp = (order: Order): number => {
    return (Number(order.createdAt) || Date.now()) + (getOrderTimeLimit(order) * 60000);
};

const hasCoordinates = (order: Order): boolean => {
    return typeof order.lat === 'number' && typeof order.lng === 'number';
};

const getHubDistance = (order: Order, config: SystemConfig): number => {
    if (!hasCoordinates(order)) return Number.POSITIVE_INFINITY;
    return DistanceService.getDistanceSync(config.businessLat, config.businessLng, order.lat as number, order.lng as number);
};

const sortOrdersByDeterministicPriority = (orders: Order[], config: SystemConfig): Order[] => {
    return [...orders].sort((left, right) => {
        const deadlineDelta = getDeadlineTimestamp(left) - getDeadlineTimestamp(right);
        if (deadlineDelta !== 0) return deadlineDelta;

        const leftDistance = getHubDistance(left, config);
        const rightDistance = getHubDistance(right, config);
        if (leftDistance !== rightDistance) return leftDistance - rightDistance;

        return left.id.localeCompare(right.id);
    });
};

const groupManualOrdersByRoute = (orders: Order[], mode: ManualMode): Array<{ groupId: string; orders: Order[] }> => {
    const grouped = new Map<string, Order[]>();
    for (const order of orders) {
        const key = order.manualRouteGroup || `${mode}_route_1`;
        const current = grouped.get(key) || [];
        grouped.set(key, [...current, order]);
    }

    return [...grouped.entries()]
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([groupId, groupedOrders]) => ({ groupId, orders: groupedOrders }));
};

const sequenceByNearestNeighbor = (orders: Order[], config: SystemConfig): Order[] => {
    if (orders.length <= 1) return [...orders];

    const remaining = [...orders];
    const ordered: Order[] = [];
    let currentLat = config.businessLat;
    let currentLng = config.businessLng;

    while (remaining.length > 0) {
        let bestIndex = 0;
        let bestDistance = Number.POSITIVE_INFINITY;

        for (let index = 0; index < remaining.length; index += 1) {
            const candidate = remaining[index];
            if (!hasCoordinates(candidate)) continue;

            const candidateDistance = DistanceService.getDistanceSync(
                currentLat,
                currentLng,
                candidate.lat as number,
                candidate.lng as number
            );

            if (candidateDistance < bestDistance) {
                bestDistance = candidateDistance;
                bestIndex = index;
            }
        }

        const [selected] = remaining.splice(bestIndex, 1);
        if (selected) {
            ordered.push(selected);
            if (hasCoordinates(selected)) {
                currentLat = selected.lat as number;
                currentLng = selected.lng as number;
            }
        }
    }

    return ordered;
};

const estimateOwnRouteCost = (
    routeOrders: Order[],
    slot: FleetSlot | null,
    config: SystemConfig
): { sequencedOrders: Order[]; cashCost: number; decisionCost: number; returnMinutes: number } => {
    const sequencedOrders = sequenceByNearestNeighbor(routeOrders, config);
    if (sequencedOrders.length === 0) {
        return { sequencedOrders, cashCost: 0, decisionCost: 0, returnMinutes: 0 };
    }

    let outboundMinutes = 0;
    let interMinutes = 0;
    let currentLat = config.businessLat;
    let currentLng = config.businessLng;

    for (let index = 0; index < sequencedOrders.length; index += 1) {
        const order = sequencedOrders[index];
        const distanceKm = DistanceService.getDistanceSync(currentLat, currentLng, order.lat as number, order.lng as number);
        const travelMinutes = DistanceService.getTravelTime(distanceKm, 'own', slot?.driver);

        if (index === 0) outboundMinutes = travelMinutes;
        else interMinutes += travelMinutes;

        order.distance = distanceKm;
        order.estimatedDuration = travelMinutes;
        currentLat = order.lat as number;
        currentLng = order.lng as number;
    }

    const returnDistanceKm = DistanceService.getDistanceSync(currentLat, currentLng, config.businessLat, config.businessLng);
    const returnMinutes = DistanceService.getTravelTime(returnDistanceKm, 'own', slot?.driver);
    const ownCost = calculateInternalCostV2(
        { outbound: outboundMinutes, inter: interMinutes, return: returnMinutes },
        false,
        sequencedOrders.length,
        0,
        config,
        1
    );

    return {
        sequencedOrders,
        cashCost: ownCost.cashCost,
        decisionCost: ownCost.totalDecisionCost,
        returnMinutes
    };
};

const estimateThirdRouteCost = (
    routeOrders: Order[],
    providers: DeliveryProvider[],
    context: OperatingContext,
    config: SystemConfig
): { cashCost: number; assigneeName: string } => {
    if (routeOrders.length === 0) return { cashCost: 0, assigneeName: 'APP TERCEIRO' };

    let cost = 0;
    const providerFrequency = new Map<string, number>();

    for (const order of routeOrders) {
        const distanceKm = getHubDistance(order, config);
        const quote = findBestProviderQuote(distanceKm, providers, context, Boolean(order.isManualCost), config);
        cost += quote.price;
        providerFrequency.set(quote.name, (providerFrequency.get(quote.name) || 0) + 1);
        order.distance = distanceKm;
        order.estimatedDuration = DistanceService.getTravelTime(distanceKm, 'third');
    }

    const discount = config.thirdPartyConfig?.batchDiscountPercentage
        ?? config.thirdPartySmartRouteDiscount
        ?? 0;
    if (routeOrders.length > 1 && discount > 0) {
        cost *= (1 - (discount / 100));
    }

    const assigneeName = [...providerFrequency.entries()]
        .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
        .map(([name]) => name)[0] || 'APP TERCEIRO';

    return { cashCost: cost, assigneeName };
};

const createDecisionStats = (decisionTrace: DecisionTraceEntry[]) => {
    return decisionTrace.reduce((acc, trace) => {
        const reason = trace.decisionReason || 'UNKNOWN';
        const mode = trace.mode || 'own';
        acc.totalDecisions += 1;
        acc.byReason[reason] = (acc.byReason[reason] || 0) + 1;
        acc.byMode[mode] = (acc.byMode[mode] || 0) + 1;
        return acc;
    }, {
        totalDecisions: 0,
        byReason: {} as Record<string, number>,
        byMode: {} as Record<string, number>
    });
};

const splitIntoChunks = <T>(items: T[], chunkSize: number): T[][] => {
    if (chunkSize <= 0) return [items];
    const chunks: T[][] = [];
    for (let index = 0; index < items.length; index += chunkSize) {
        chunks.push(items.slice(index, index + chunkSize));
    }
    return chunks;
};

const isDeliveryPendingOrder = (order: Order): boolean => {
    if (order.type !== 'DELIVERY') return false;
    if (!DELIVERY_PENDING_STATUSES.has(order.status)) return false;
    if ((order.status === 'assigned' || order.status === 'dispatched') && order.pickupConfirmed) return false;
    return true;
};

const pickOwnSlotIndex = (
    slots: Array<{ slot: FleetSlot; orders: Order[] }>,
    routeLimit: number,
    order: Order,
    config: SystemConfig
): number => {
    const distanceFromHub = getHubDistance(order, config);

    const ranked = slots
        .map((entry, index) => ({ entry, index }))
        .filter(({ entry }) => entry.orders.length < routeLimit)
        .filter(({ entry }) => {
            const maxRange = entry.slot.driver.maxRangeKm;
            if (!maxRange) return true;
            return distanceFromHub <= maxRange;
        })
        .sort((left, right) => {
            if (left.entry.orders.length !== right.entry.orders.length) {
                return left.entry.orders.length - right.entry.orders.length;
            }
            if (left.entry.slot.delayMins !== right.entry.slot.delayMins) {
                return left.entry.slot.delayMins - right.entry.slot.delayMins;
            }
            return left.entry.slot.driverId.localeCompare(right.entry.slot.driverId);
        });

    return ranked[0]?.index ?? -1;
};

/**
 * Modo simplificado de recomendacao.
 *
 * Diferenca para o modo avancado:
 * - Advanced: arbitragem multi-sinal, estrategias divergentes (boomerang/retencao) e heuristicas dinamicas.
 * - Simplified: nao usa Oracle/arbitragem dinamica/estrategias divergentes; aplica prioridade por prazo+distancia,
 *   alocacao por capacidade fixa e fallback claro para terceiro.
 */
export const calculateSimplifiedSplitAsync = async (
    orders: Order[],
    config: SystemConfig,
    drivers: Driver[],
    providers: DeliveryProvider[],
    context: OperatingContext
): Promise<SmartSplitSuggestion> => {
    const pendingOrders = orders.filter(isDeliveryPendingOrder);
    if (pendingOrders.length === 0) return createEmptySuggestion();

    const nowMs = Date.now();
    const ownFleetOrders: Order[] = [];
    const thirdPartyOrders: Order[] = [];
    const heldOrders: Order[] = [];
    const routes: SuggestedRoute[] = [];
    const decisionTrace: DecisionTraceEntry[] = [];
    const processedOrderIds = new Set<string>();

    let ownCash = 0;
    let ownDecision = 0;
    let thirdCash = 0;
    let thirdDecision = 0;

    const fleetSlots = getFleetAvailability(drivers, orders, config.businessLat, config.businessLng, config)
        .filter((slot) => slot.isAvailable);
    const routeLimit = Math.max(1, Math.min(config.fleetConfig.maxOrdersPerRoute || 4, 4));
    const thirdRouteLimit = Math.max(1, Math.min(config.thirdPartyConfig.maxOrdersPerRoute || 2, 4));

    const slotAllocations = fleetSlots.map((slot) => ({ slot, orders: [] as Order[] }));
    const ownCapacity = slotAllocations.length * routeLimit;
    const maxOwnDispatchableOrders = ownCapacity === 0
        ? 0
        : Math.max(1, Math.floor(ownCapacity * OWN_CAPACITY_BUFFER_RATIO));

    const manualOrders = pendingOrders.filter((order) => !!order.manualAssignment);
    const autoOrders = pendingOrders.filter((order) => !order.manualAssignment);

    const manualOwnGroups = groupManualOrdersByRoute(
        manualOrders.filter((order) => order.manualAssignment === 'own'),
        'own'
    );
    for (const group of manualOwnGroups) {
        const validOrders = group.orders.filter(hasCoordinates);
        const invalidOrders = group.orders.filter((order) => !hasCoordinates(order));

        for (const order of invalidOrders) {
            heldOrders.push(order);
            processedOrderIds.add(order.id);
            decisionTrace.push({
                kind: 'strategy',
                mode: 'hold',
                decisionReason: 'CONSTRAINT_NO_FLEET',
                orderIds: [order.id],
                chosenLabel: 'MISSING_COORDINATES',
                rationale: 'Modo simplificado: pedido sem coordenadas foi retido para geocodificacao.',
                constraints: ['simplified_mode', 'geocoding_missing']
            });
        }

        if (validOrders.length === 0) continue;

        const slotIndex = slotAllocations.findIndex((allocation) => allocation.orders.length === 0);
        const slot = slotIndex >= 0 ? slotAllocations[slotIndex].slot : null;
        const ownRoute = estimateOwnRouteCost(validOrders, slot, config);

        routes.push({
            type: 'own',
            assigneeId: slot?.driverId,
            assigneeName: slot?.driver.name || 'FROTA PROPRIA (MANUAL)',
            orders: ownRoute.sequencedOrders,
            cost: ownRoute.cashCost,
            strategyLabel: 'MANUAL (SIMPLIFIED): FROTA PROPRIA',
            decisionSummary: 'Modo simplificado: atribuicao manual preservada para frota propria.',
            requiresOvertimeApproval: slot?.requiresOvertimeApproval,
            estimatedReturnMinutes: ownRoute.returnMinutes
        });

        // v59.2: Calcular custo de terceiros para comparativo (evita "APP n/d")
        const thirdCostForComparison = estimateThirdRouteCost(validOrders, providers, context, config);

        decisionTrace.push({
            kind: 'route',
            mode: 'own',
            decisionReason: 'UNKNOWN',
            orderIds: ownRoute.sequencedOrders.map((order) => order.id),
            chosenLabel: 'MANUAL_OWN_SIMPLIFIED',
            rationale: 'Modo simplificado: soberania do operador preservada para rota manual da frota propria.',
            ownCost: ownRoute.cashCost,
            thirdCost: thirdCostForComparison.cashCost,
            constraints: ['manual_assignment', 'simplified_mode']
        });

        for (const order of ownRoute.sequencedOrders) {
            order.optimizationOutcome = 'own';
            ownFleetOrders.push(order);
            processedOrderIds.add(order.id);
        }

        ownCash += ownRoute.cashCost;
        ownDecision += ownRoute.decisionCost;
    }

    const manualThirdGroups = groupManualOrdersByRoute(
        manualOrders.filter((order) => order.manualAssignment === 'third'),
        'third'
    );
    for (const group of manualThirdGroups) {
        const validOrders = group.orders.filter(hasCoordinates);
        const invalidOrders = group.orders.filter((order) => !hasCoordinates(order));

        for (const order of invalidOrders) {
            heldOrders.push(order);
            processedOrderIds.add(order.id);
            decisionTrace.push({
                kind: 'strategy',
                mode: 'hold',
                decisionReason: 'CONSTRAINT_NO_FLEET',
                orderIds: [order.id],
                chosenLabel: 'MISSING_COORDINATES',
                rationale: 'Modo simplificado: pedido sem coordenadas foi retido para geocodificacao.',
                constraints: ['simplified_mode', 'geocoding_missing']
            });
        }

        if (validOrders.length === 0) continue;

        const sequencedOrders = sequenceByNearestNeighbor(validOrders, config);
        const thirdRoute = estimateThirdRouteCost(sequencedOrders, providers, context, config);

        routes.push({
            type: 'third',
            assigneeName: `${thirdRoute.assigneeName} (MANUAL)`,
            orders: sequencedOrders,
            cost: thirdRoute.cashCost,
            strategyLabel: 'MANUAL (SIMPLIFIED): APP TERCEIRO',
            decisionSummary: 'Modo simplificado: atribuicao manual preservada para app terceiro.'
        });

        // v59.2: Calcular custo da frota propria para comparativo (evita "FROTA n/d")
        const ownCostForComparison = estimateOwnRouteCost(validOrders, null, config);

        decisionTrace.push({
            kind: 'route',
            mode: 'third',
            decisionReason: 'UNKNOWN',
            orderIds: sequencedOrders.map((order) => order.id),
            chosenLabel: 'MANUAL_THIRD_SIMPLIFIED',
            rationale: 'Modo simplificado: soberania do operador preservada para rota manual externa.',
            ownCost: ownCostForComparison.cashCost,
            thirdCost: thirdRoute.cashCost,
            constraints: ['manual_assignment', 'simplified_mode']
        });

        for (const order of sequencedOrders) {
            order.optimizationOutcome = 'third';
            thirdPartyOrders.push(order);
            processedOrderIds.add(order.id);
        }

        thirdCash += thirdRoute.cashCost;
        thirdDecision += thirdRoute.cashCost;
    }

    const sortedAutoOrders = sortOrdersByDeterministicPriority(
        autoOrders.filter((order) => !processedOrderIds.has(order.id)),
        config
    );
    const pendingThirdQueue: SimplifiedDecision[] = [];

    for (const order of sortedAutoOrders) {
        if (processedOrderIds.has(order.id)) continue;

        if (!hasCoordinates(order)) {
            heldOrders.push(order);
            processedOrderIds.add(order.id);
            decisionTrace.push({
                kind: 'strategy',
                mode: 'hold',
                decisionReason: 'CONSTRAINT_NO_FLEET',
                orderIds: [order.id],
                chosenLabel: 'MISSING_COORDINATES',
                rationale: 'Modo simplificado: pedido sem coordenadas foi retido para geocodificacao.',
                constraints: ['simplified_mode', 'geocoding_missing']
            });
            continue;
        }

        const slotIndex = pickOwnSlotIndex(slotAllocations, routeLimit, order, config);
        const hubDistance = getHubDistance(order, config);
        const elapsedMinutes = getElapsedMinutes(order, nowMs);
        const timeLimit = getOrderTimeLimit(order);
        const thirdEtaMinutes = DistanceService.getTravelTime(hubDistance, 'third') + (config.thirdPartyConfig.serviceTimeMinutes || 5);

        if (slotIndex === -1) {
            pendingThirdQueue.push({
                order,
                reason: 'Sem slot elegivel de frota propria no momento.',
                thirdEtaMinutes,
                thirdSlackMinutes: timeLimit - elapsedMinutes - thirdEtaMinutes
            });
            processedOrderIds.add(order.id);
            continue;
        }

        const selectedSlot = slotAllocations[slotIndex].slot;
        const ownEtaMinutes = selectedSlot.delayMins + DistanceService.getTravelTime(hubDistance, 'own', selectedSlot.driver) + (config.fleetConfig.serviceTimeMinutes || 5);
        const ownSlackMinutes = timeLimit - elapsedMinutes - ownEtaMinutes;
        const thirdSlackMinutes = timeLimit - elapsedMinutes - thirdEtaMinutes;

        const ownCapacityGuardReached = ownFleetOrders.length >= maxOwnDispatchableOrders && maxOwnDispatchableOrders > 0;
        const shouldFallbackToThird = ownSlackMinutes < 0 && thirdSlackMinutes > ownSlackMinutes;

        if (ownCapacityGuardReached || shouldFallbackToThird) {
            pendingThirdQueue.push({
                order,
                reason: ownCapacityGuardReached
                    ? 'Capacidade planejada da frota propria atingida; excedente para app terceiro.'
                    : 'Pedido critico: app terceiro oferece melhor slack de SLA que a frota propria.',
                ownEtaMinutes,
                thirdEtaMinutes,
                ownSlackMinutes,
                thirdSlackMinutes
            });
            processedOrderIds.add(order.id);
            continue;
        }

        slotAllocations[slotIndex].orders.push(order);
        ownFleetOrders.push(order);
        order.optimizationOutcome = 'own';
        processedOrderIds.add(order.id);
    }

    for (const allocation of slotAllocations) {
        if (allocation.orders.length === 0) continue;

        const ownRoute = estimateOwnRouteCost(allocation.orders, allocation.slot, config);
        routes.push({
            type: 'own',
            assigneeId: allocation.slot.driverId,
            assigneeName: allocation.slot.driver.name,
            orders: ownRoute.sequencedOrders,
            cost: ownRoute.cashCost,
            strategyLabel: 'SIMPLIFIED: FROTA PROPRIA',
            decisionSummary: 'Modo simplificado: rota sequenciada por prazo e proximidade com capacidade fixa.',
            requiresOvertimeApproval: allocation.slot.requiresOvertimeApproval,
            estimatedReturnMinutes: ownRoute.returnMinutes
        });

        ownCash += ownRoute.cashCost;
        ownDecision += ownRoute.decisionCost;

        // v59.2: Calcular custo de terceiros para comparativo (evita "APP n/d")
        const thirdCostForComparison = estimateThirdRouteCost(allocation.orders, providers, context, config);

        decisionTrace.push({
            kind: 'route',
            mode: 'own',
            decisionReason: 'CHEAPER',
            orderIds: ownRoute.sequencedOrders.map((order) => order.id),
            chosenLabel: 'OWN_SIMPLIFIED_ROUTE',
            rationale: 'Modo simplificado: frota propria priorizada com regras fixas de capacidade e sequenciamento deterministico.',
            ownCost: ownRoute.cashCost,
            thirdCost: thirdCostForComparison.cashCost,
            constraints: ['simplified_mode', 'deterministic_priority', 'capacity_guard']
        });
    }

    const thirdOrdersSorted = sortOrdersByDeterministicPriority(
        pendingThirdQueue.map((decision) => decision.order),
        config
    );
    const thirdOrderChunks = splitIntoChunks(thirdOrdersSorted, thirdRouteLimit);

    for (const chunk of thirdOrderChunks) {
        const sequencedOrders = sequenceByNearestNeighbor(chunk, config);
        const thirdRoute = estimateThirdRouteCost(sequencedOrders, providers, context, config);

        routes.push({
            type: 'third',
            assigneeName: thirdRoute.assigneeName,
            orders: sequencedOrders,
            cost: thirdRoute.cashCost,
            strategyLabel: 'SIMPLIFIED: APP TERCEIRO',
            decisionSummary: 'Modo simplificado: rota externa para excedente de capacidade ou protecao de SLA critico.'
        });

        for (const order of sequencedOrders) {
            order.optimizationOutcome = 'third';
            thirdPartyOrders.push(order);
        }

        thirdCash += thirdRoute.cashCost;
        thirdDecision += thirdRoute.cashCost;

        const reasons = sequencedOrders
            .map((order) => pendingThirdQueue.find((decision) => decision.order.id === order.id)?.reason)
            .filter((reason): reason is string => Boolean(reason));

        // v59.2: Calcular custo da frota propria para comparativo (evita "FROTA n/d")
        const ownCostForComparison = estimateOwnRouteCost(chunk, null, config);

        decisionTrace.push({
            kind: 'route',
            mode: 'third',
            decisionReason: 'SLA_PRIORITY',
            orderIds: sequencedOrders.map((order) => order.id),
            chosenLabel: 'THIRD_SIMPLIFIED_ROUTE',
            rationale: `Modo simplificado: ${reasons[0] || 'alocacao externa por regra operacional.'}`,
            ownCost: ownCostForComparison.cashCost,
            thirdCost: thirdRoute.cashCost,
            constraints: ['simplified_mode', 'deterministic_priority', 'sla_protection', 'capacity_guard']
        });
    }

    const allOrderedForSla = [...ownFleetOrders, ...thirdPartyOrders];
    const lowestSlack = allOrderedForSla.length === 0
        ? 999
        : Math.min(...allOrderedForSla.map((order) => getOrderTimeLimit(order) - getElapsedMinutes(order, nowMs)));

    return {
        ownFleet: ownFleetOrders,
        thirdParty: thirdPartyOrders,
        heldOrders,
        routes,
        thirdPartyTripsCount: routes.filter((route) => route.type === 'third').length,
        metrics: {
            breakdown: { ownCash, thirdPartyCash: thirdCash, ownDecision, thirdPartyDecision: thirdDecision },
            cashDelta: {
                chosen: ownCash + thirdCash,
                alternative: 0,
                signedDelta: 0,
                label: 'EFICIENCIA_ATUAL',
                reason: routes.some((route) => route.type === 'third') ? 'SLA_PRIORITY' : 'CHEAPER'
            },
            decisionStats: createDecisionStats(decisionTrace)
        },
        isSplitRecommended: routes.length > 0,
        optimizationConstrainedBySLA: false,
        estimatedTime: 0,
        minSla: lowestSlack,
        lowestSlack,
        decisionTrace,
        suggestedStrategies: []
    };
};
